import { Injectable } from '@angular/core';
import { RequestStatusService } from '../reports/request-status.service';
import { SpinnerService } from '../spinner/spinner.service';
import { ToastrService } from 'ngx-toastr';
import { saveAs } from 'file-saver';
import autoTable from 'jspdf-autotable';

@Injectable({
  providedIn: 'root'
})
export class DigitalCopyPdfService {

  constructor(
    private requestStatusService: RequestStatusService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService
  ) { }

  /**
   * Format monetary values with $ symbol and comma separators
   */
  private formatCurrency(value: any): string {
    if (value == null || value === '' || isNaN(Number(value))) {
      return '';
    }
    const numValue = Number(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  /**
   * Download a digital copy PDF for the given adjReqId (row), using the same logic as RequestStatusComponent's onGenerate().
   * This method does not preview, it directly downloads the PDF as Digital_Copy_{adjReqId}.pdf
   */
  async downloadDigitalCopy(adjReqId: string | number) {
    console.log('downloadDigitalCopy called with adjReqId:', adjReqId);
    const adjReqIdStr = adjReqId != null ? String(adjReqId).trim() : '';
    if (!adjReqIdStr) {
      this.toastr.error('Invalid Adjustment Request ID.', 'Error');
      return;
    }

    console.log('About to show spinner...');
    this.spinnerService.showSpinner();

    try {
      // Validate AdjReqId (optional, can skip if not needed)
      const isValid = await this.requestStatusService.validateAdjReqId(adjReqIdStr);
      if (!isValid) {
        this.spinnerService.hideSpinner();
        this.toastr.error('Invalid Adjustment Request ID: not found in system.', 'Error');
        return;
      }

      // Fetch invoice data
      const invoiceData = await this.requestStatusService.getInvoiceDataForAdjReqId(adjReqIdStr);
      if (!invoiceData) {
        this.spinnerService.hideSpinner();
        this.toastr.error('Failed to fetch invoice data for PDF.', 'Error');
        return;
      }

      // Dynamically import jsPDF and autoTable
      const jsPDFModule = await import('jspdf');
      const autoTableModule = await import('jspdf-autotable');    
          const jsPDF = jsPDFModule.jsPDF;
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          // Set PDF metadata title for Chrome/Edge/Adobe compatibility
          // if (this.reportTypeFlag === 'DigitalCopies') {
          //   doc.setProperties({
          //     title: `Digital_Copy_${adjReqId || ''}`
          //   });
          // } else {
          //   doc.setProperties({
          //     title: 'AdjustmentRequest_Sample'
          //   });
          // }
          doc.setProperties({
            title: `Digital_Copy_${adjReqId || ''}`
          });
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          // UPS brown and yellow
          const brown: [number, number, number] = [53, 28, 21]; // #351c15
          const yellow: [number, number, number] = [255, 204, 0]; // #ffcc00
          let y = 36;
          // Header
          doc.setFillColor(brown[0], brown[1], brown[2]);
          doc.setDrawColor(brown[0], brown[1], brown[2]);
          doc.rect(30, y, pageWidth-60, 28, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10.4); // 13 * 0.8
          doc.setTextColor(yellow[0], yellow[1], yellow[2]);
          doc.text('UPS MAIL INNOVATIONS  ACCOUNTS RECEIVABLE ADJUSTMENT AUTHORIZATION', pageWidth / 2, y+18, { align: 'center' });
          // Add menu/filename at the top right for DigitalCopies
          //if (this.reportTypeFlag === 'DigitalCopies') {
            const menuLabel = `Digital_Copy_${adjReqId || ''}`;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(menuLabel, pageWidth - 40, y + 18, { align: 'right' });
          //}
          y += 38;
          // Description (left)
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8); // 10 * 0.8
          doc.setTextColor(brown[0], brown[1], brown[2]);
          doc.text('Description of adjustment/error & root cause :', 40, y);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(0,0,0);
          let descLines = doc.splitTextToSize(invoiceData.description, 350);
          doc.text(descLines, 40, y+13);

          // Declare leftColBottom and controlsY before conditional blocks
          let leftColBottom: number;
          let controlsY: number;

          // Controls/ corrections - MAKE THIS CONDITIONAL
          if (!invoiceData.isBulkRequest) {
            // Controls (left, below desc)
            controlsY = y + 13 + descLines.length*10 + 14;
            doc.setDrawColor(brown[0], brown[1], brown[2]);
            doc.line(40, controlsY-7, 390, controlsY-7);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(brown[0], brown[1], brown[2]);
            doc.text('Controls/ corrections put in place to prevent recurrence of issue :', 40, controlsY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(0,0,0);
            // Add text wrapping like description field
            let controlsLines = doc.splitTextToSize(invoiceData.controls, 350);
            doc.text(controlsLines, 40, controlsY+13);
            
            // Update leftColBottom calculation to account for wrapped text
            leftColBottom = controlsY + 13 + controlsLines.length * 10 + 6;
          } else {
            // For bulk requests, position additional fields directly after description
            controlsY = y + 13 + descLines.length*10 + 14; // Use same calculation as base
            
            // Add new text field beneath Description with space above (for bulk requests only)
            let additionalFieldY = controlsY + 20; // 20pt space above the new field
            doc.setDrawColor(brown[0], brown[1], brown[2]);
            doc.line(40, additionalFieldY - 7, 390, additionalFieldY - 7);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(brown[0], brown[1], brown[2]);
            doc.text('Special Instructions:', 40, additionalFieldY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(0, 0, 0);
            // Split text to fit within the column width and handle empty/null values
            let specialInstructionsText = invoiceData.specialInstructions || 'No special instructions provided';
            let additionalInfoLines = doc.splitTextToSize(specialInstructionsText, 350);
            doc.text(additionalInfoLines, 40, additionalFieldY + 13);
            
            // Add another text field beneath Special Instructions with space above
            let secondAdditionalFieldY = additionalFieldY + 13 + additionalInfoLines.length * 10 + 20; // 20pt space above, accounting for actual text height
            doc.setDrawColor(brown[0], brown[1], brown[2]);
            doc.line(40, secondAdditionalFieldY - 7, 390, secondAdditionalFieldY - 7);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(brown[0], brown[1], brown[2]);
            doc.text('# of Customers:', 40, secondAdditionalFieldY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(0, 0, 0);
            // Split text to fit within the column width and handle empty/null values
            let customerCountText = invoiceData.customerCount || 'Not specified';
            let secondAdditionalInfoLines = doc.splitTextToSize(customerCountText, 350);
            doc.text(secondAdditionalInfoLines, 40, secondAdditionalFieldY + 13);
            
            // Update leftColBottom to include both new fields with accurate spacing calculation
            leftColBottom = secondAdditionalFieldY + 13 + secondAdditionalInfoLines.length * 10 + 6;
          }

          // Adjustment reason (right) - this remains always visible
          let adjX = 420, adjW = pageWidth-420-40;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(brown[0], brown[1], brown[2]);
          doc.text('Adjustment reason', adjX, y);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(0,0,0);
          let adjReasonLines = invoiceData.adjustmentReasons.map(r=>doc.splitTextToSize(r, adjW-20)).flat();
          doc.text(adjReasonLines, adjX, y+13);
          // Declare checkRefundY outside the conditional block so it's always defined
          let checkRefundY: number = y+13+adjReasonLines.length*10+6; // Default value

          // Checkboxes (clean shapes) - CONDITIONAL
          if (!invoiceData.isBulkRequest) {
            let checkboxY = y+13+adjReasonLines.length*10+6;
            let checkboxRows = [
              { label: 'Billed within 6 months', value: invoiceData.billedSixMonths },
              { label: 'Disputed within 6 months', value: invoiceData.disputedSixMonths },
              { label: 'Billing Edit required', value: invoiceData.billingEditRequired },
              { label: 'Billing Edit completed', value: invoiceData.billingEditCompleted }
            ];
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(0,0,0);
            checkboxRows.forEach((row, idx) => {
              let yPos = checkboxY + idx*18;
              // Set label to UPS brown
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(8);
              doc.setTextColor(brown[0], brown[1], brown[2]);
              doc.text(row.label, adjX, yPos+8);
              // Draw single box
              doc.setDrawColor(brown[0], brown[1], brown[2]);
              doc.setFillColor(255,255,255);
              doc.rect(adjX+170, yPos, 24, 18, 'FD');
              // Map value to Y/N/N/A
              let val = (row.value || '').toString().trim().toLowerCase();
              let displayVal = '';
              let color = brown;
              if (val === 'yes') {
                displayVal = 'Y';
                color = [0, 150, 0]; // green
              } else if (val === 'no') {
                displayVal = 'N';
                color = [150, 0, 0]; // red
              } else if (val === 'n/a' || val === 'na') {
                displayVal = 'N/A';
                color = [120, 120, 120]; // gray
              }
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(13);
              doc.setTextColor(color[0], color[1], color[2]);
              // Center value in the single box
              doc.text(displayVal, adjX+182, yPos+12, {align:'center'});
            });
            
            // Check refund - ALSO CONDITIONAL (moved inside the if block)
            checkRefundY = checkboxY + checkboxRows.length*18 + 8;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(brown[0], brown[1], brown[2]);
            doc.text('Check refund', adjX, checkRefundY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(0,0,0);
            let checkRefundLines = doc.splitTextToSize(invoiceData.checkRefund, adjW-150);
            doc.text(checkRefundLines, adjX+100, checkRefundY);
            
            // Check refund comments (positioned below check refund with proper spacing) - ALSO CONDITIONAL
            let checkRefundCommentsY = checkRefundY + Math.max(checkRefundLines.length * 10, 14) + 12;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(brown[0], brown[1], brown[2]);
            doc.text('Check refund comments', adjX, checkRefundCommentsY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(0,0,0);
            let checkRefundCommentsText = invoiceData.checkRefundComments || 'No comments available';
            let checkRefundCommentsLines = doc.splitTextToSize(checkRefundCommentsText, adjW-150);
            doc.text(checkRefundCommentsLines, adjX+100, checkRefundCommentsY);
            
            // Update rightColEnd calculation to include check refund sections
            var rightColEnd = checkRefundCommentsY + Math.max(checkRefundCommentsLines.length * 10, 14) + 20;
          } else {
            // If not a bulk request, set rightColEnd to just after adjustment reasons
            var rightColEnd = y+13+adjReasonLines.length*10+40; // Add some padding
          }
          
          // ADJ REQ ID# (positioned conditionally based on whether check refund sections exist)
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(brown[0], brown[1], brown[2]);
          let adjReqIdY = invoiceData.isBulkRequest ? checkRefundY-12 : y+13+adjReasonLines.length*10+20;
          doc.text('ADJ REQ ID#', adjX+adjW-120, adjReqIdY);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(0,0,0);
          doc.text(invoiceData.adjReqId, adjX+adjW-30, adjReqIdY, {align:'right'});
          
          // Calculate the bottom Y position after all top content
          let contentBottom = Math.max(leftColBottom, rightColEnd);

          // If the table would overlap with the content, start a new page
          let minTableY = contentBottom + 32; // add more space between content and heading
          let tableHeadingY = minTableY;
          // If the heading or table would overlap the page bottom, start a new page
          if (tableHeadingY + 32 > pageHeight - 60) { // ensure at least 32pt below heading
            doc.addPage();
            tableHeadingY = 40; // top margin for new page
          }

          // Draw the heading first, then table below
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9.6); // 12 * 0.8
          doc.setTextColor(brown[0], brown[1], brown[2]);
          doc.text('BILLING ADJUSTMENT SUMMARY', 40, tableHeadingY);
          // Table columns as in screenshot - MAKE HEADERS CONDITIONAL
          let tableHead: string[][];
          let tableFoot: string[][]; // Define tableFoot for footer row(s)
          if (!invoiceData.isBulkRequest) {
            // Non-bulk request: show all headers including the conditional ones
            tableHead = [[
              '', 'MMS#', 'CMF#', 'Customer Name', 'Orig Stmt #', 'Inv date', 'WE Period', 'TOTAL INVOICE', 'CORRECT CHARGES', 'NET ADJUSTMENT', 'ACTION', 'Billing Site', 'AE'
            ]];
            
            // Initialize footer with empty strings
            let footerRow = Array(13).fill(''); // 13 columns for non-bulk request
            footerRow[6] = 'TOTAL ADJUST'; // Position 6 for the label
            
            // Fill positions 7, 8, 9 from invoiceData.summaryFoot array
            if (invoiceData.summaryFoot && Array.isArray(invoiceData.summaryFoot)) {
              // Position 7: TOTAL INVOICE
              if (invoiceData.summaryFoot[7] != null && invoiceData.summaryFoot[7] !== '') {
                const totalInvoiceValue = parseFloat(invoiceData.summaryFoot[7]);
                footerRow[7] = !isNaN(totalInvoiceValue) ? this.formatCurrency(totalInvoiceValue) : invoiceData.summaryFoot[7];
              }
              
              // Position 8: CORRECT CHARGES
              if (invoiceData.summaryFoot[8] != null && invoiceData.summaryFoot[8] !== '') {
                const correctChargesValue = parseFloat(invoiceData.summaryFoot[8]);
                footerRow[8] = !isNaN(correctChargesValue) ? this.formatCurrency(correctChargesValue) : invoiceData.summaryFoot[8];
              }
              
              // Position 9: NET ADJUSTMENT
              if (invoiceData.summaryFoot[9] != null && invoiceData.summaryFoot[9] !== '') {
                const netAdjustmentValue = parseFloat(invoiceData.summaryFoot[9]);
                footerRow[9] = !isNaN(netAdjustmentValue) ? this.formatCurrency(netAdjustmentValue) : invoiceData.summaryFoot[9];
              }
            } else {
              // Fallback: use individual properties if summaryFoot array is not available
              if (invoiceData.totalInvoice != null && invoiceData.totalInvoice !== '') {
                const totalInvoiceValue = parseFloat(invoiceData.totalInvoice);
                footerRow[7] = !isNaN(totalInvoiceValue) ? this.formatCurrency(totalInvoiceValue) : invoiceData.totalInvoice;
              }
              
              if (invoiceData.correctCharges != null && invoiceData.correctCharges !== '') {
                const correctChargesValue = parseFloat(invoiceData.correctCharges);
                footerRow[8] = !isNaN(correctChargesValue) ? this.formatCurrency(correctChargesValue) : invoiceData.correctCharges;
              }
              
              if (invoiceData.netAdjustment != null && invoiceData.netAdjustment !== '') {
                const netAdjustmentValue = parseFloat(invoiceData.netAdjustment);
                footerRow[9] = !isNaN(netAdjustmentValue) ? this.formatCurrency(netAdjustmentValue) : invoiceData.netAdjustment;
              }
            }
            
            tableFoot = [footerRow];
          } else {
            // Bulk request: exclude CMF#, Orig Stmt #, Inv date, WE Period, TOTAL INVOICE, CORRECT CHARGES, and ACTION but KEEP Billing Site and AE, and add Adjustment Method
            tableHead = [[
              '', 'MMS#', 'Customer Name', 'Amount', 'Billing Site', 'AE', 'Adjustment Method', 'Check Request', 'Remit Address'
            ]];
            
            // Initialize footer for bulk request (9 columns now - increased from 8)
            let footerRow = Array(9).fill('');
            footerRow[2] = 'TOTAL ADJUST'; // Position 2 for the label in filtered array
            
            // For bulk requests, fill position 3 (AMOUNT only in filtered array)
            if (invoiceData.summaryFoot && Array.isArray(invoiceData.summaryFoot)) {
              // Map from original position 9 (NET ADJUSTMENT) -> filtered position 3 (AMOUNT)
              if (invoiceData.summaryFoot[9] != null && invoiceData.summaryFoot[9] !== '') {
                const netAdjustmentValue = parseFloat(invoiceData.summaryFoot[9]);
                footerRow[3] = !isNaN(netAdjustmentValue) ? this.formatCurrency(netAdjustmentValue) : invoiceData.summaryFoot[9];
              }
            } else {
              // Fallback for bulk requests
              if (invoiceData.netAdjustment != null && invoiceData.netAdjustment !== '') {
                const netAdjustmentValue = parseFloat(invoiceData.netAdjustment);
                footerRow[3] = !isNaN(netAdjustmentValue) ? this.formatCurrency(netAdjustmentValue) : invoiceData.netAdjustment;
              }
            }
            
            tableFoot = [footerRow];
          }

          // Add row labels (A, B, C) as first column and format currency for columns - ADJUST COLUMN INDICES
          const tableBody = invoiceData.summary.map(row => {
            if (!invoiceData.isBulkRequest) {
              // Non-bulk request: format currency for columns 7, 8, 9 (TOTAL INVOICE, CORRECT CHARGES, NET ADJUSTMENT)
              return row.map((cell, index) => {
                if ((index === 7 || index === 8 || index === 9) && cell != null && cell !== '') {
                  const numValue = parseFloat(cell);
                  return !isNaN(numValue) ? this.formatCurrency(numValue) : cell;
                }
                return cell;
              });
            } else {
              // Bulk request: filter out columns but KEEP Billing Site (11), AE (12), and add Adjustment Method (13)
              // Remove columns: CMF# (2), Orig Stmt # (4), Inv date (5), WE Period (6), TOTAL INVOICE (7), CORRECT CHARGES (8), ACTION (10)
              const filteredRow = row.filter((cell, index) => 
                index !== 2 && index !== 4 && index !== 5 && index !== 6 && index !== 7 && index !== 8 && index !== 10
              );
              
              // After filtering, AMOUNT is at index 3
              return filteredRow.map((cell, index) => {
                if (index === 3 && cell != null && cell !== '') {
                  const numValue = parseFloat(cell);
                  return !isNaN(numValue) ? this.formatCurrency(numValue) : cell;
                }
                return cell;
              });
            }
          });

                   
        

          // Define dynamic columnStyles based on request type
          let columnStyles: any = {};
          if (!invoiceData.isBulkRequest) {
            columnStyles = {
              7: { halign: 'right' }, // TOTAL INVOICE
              8: { halign: 'right' }, // CORRECT CHARGES
              9: { halign: 'right' }  // NET ADJUSTMENT
            };          
          } else {
            columnStyles = {
              3: { halign: 'right' }  // AMOUNT (moved to index 3)
            };
          }

          // Remove the temporary document calculation and just render the table once
          // Single table render with proper pagination handling
          autoTable(doc, {
            startY: tableHeadingY + 8,
            head: tableHead,
            body: tableBody,
            foot: tableFoot,
            theme: 'grid',
            headStyles: { fillColor: yellow, textColor: brown, fontStyle: 'bold', fontSize: 8, halign: 'center' },
            bodyStyles: { fontSize: 8, fillColor: [255,255,255], textColor: brown, halign: 'center', valign: 'middle' },
            footStyles: { fillColor: yellow, textColor: brown, fontStyle: 'bold', fontSize: 6, halign: 'center' },
            columnStyles: columnStyles,
            margin: { left: 30, right: 30 },
            tableWidth: 'auto',
            styles: { cellPadding: 2.4, lineColor: brown, lineWidth: 0.5 },
            showHead: 'everyPage', // Show header on every page
            showFoot: 'lastPage', // Show footer only on last page
            pageBreak: 'auto', // Allow automatic page breaks
            //rowPageBreak : 'auto',
            // pageBreakBefore: function(cursor, doc) {
            //   // Only break if we're getting close to the bottom of the page
            //   return cursor.y >= pageHeight - 100;
            // },
            // pageBreakBefore: function(cursor, doc) {
            //   // Correct parameters: cursor has y property, doc is the document
            //   return cursor.y >= pageHeight - 120; // Break when close to bottom
            // },
            didDrawPage: function(data) {
              // Add page numbers for multi-page tables (starting from page 2)
              if (data.pageNumber > 1) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(80, 80, 80);
                doc.text(`Page ${data.pageNumber}`, pageWidth - 50, pageHeight - 30, { align: 'right' });
              }
            },
          });

          // Get the final Y position after the table
          let afterTableY = (doc as any).lastAutoTable.finalY + 14;

          // Check if we need to add a new page for the bottom content
          const remainingSpace = pageHeight - afterTableY;
          const requiredSpaceForBottomContent = 200; // Estimate space needed for approval comments, circles, etc.

          if (remainingSpace < requiredSpaceForBottomContent) {
            doc.addPage();
            afterTableY = 40; // Start from top of new page
          }

          // Continue with approval comments and other content...
          // Approval Comments (replacing the approval thresholds box)
          let approvalCommentsY = afterTableY + 14;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9.6); // 12 * 0.8
          doc.setTextColor(brown[0], brown[1], brown[2]);
          doc.text('APPROVAL COMMENTS', 40, approvalCommentsY);

          // Draw underline manually
          const approvalCommentsTextWidth = doc.getTextWidth('APPROVAL COMMENTS');
          doc.setDrawColor(brown[0], brown[1], brown[2]);
          doc.setLineWidth(1);
          doc.line(40, approvalCommentsY+3, 40+approvalCommentsTextWidth, approvalCommentsY+3);

          // Approval comments content
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(0,0,0);
         
          let secondLevelComment = invoiceData.SecondLevelRemrks || '';
          let approvalCommentsText = invoiceData.approvalComments || '';
          if (approvalCommentsText || secondLevelComment) {
            approvalCommentsText = `1st level comments : ${approvalCommentsText || 'N/A'}\n2nd level comments : ${secondLevelComment || 'N/A'}`;
          }
          if (!approvalCommentsText) {
            approvalCommentsText = 'No approval comments available';
          }
          let approvalCommentsLines = doc.splitTextToSize(approvalCommentsText, 400);
          doc.text(approvalCommentsLines, 40, approvalCommentsY+16);

          // Calculate the bottom Y position for the approval comments section
          let approvalCommentsBottomY = approvalCommentsY + 16 + Math.max(approvalCommentsLines.length * 10, 20);

          // Rest of the content continues as before...
          // Color coding legend/disclaimer
          let legendY = approvalCommentsBottomY + 12;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(brown[0], brown[1], brown[2]);
          doc.text('APPROVAL STATUS COLOR LEGEND:', 40, legendY);

          // Green indicator - properly aligned
          doc.setFillColor(46, 204, 113);
          doc.setDrawColor(brown[0], brown[1], brown[2]);
          doc.circle(60, legendY + 15, 7, 'FD');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0);
          doc.text('Green = Stage Completed/Approved', 75, legendY + 18);

          // White indicator - properly aligned
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(brown[0], brown[1], brown[2]);
          doc.circle(60, legendY + 30, 7, 'FD');
          doc.text('White = Stage Pending/Awaiting Action', 75, legendY + 33);

          approvalCommentsBottomY = legendY + 45; // Update bottom position
          
          // Approval Circles and right-side content (horizontal alignment, UPS theme, no overlap)
          // Position to the right of the approval comments, at the same Y level
          const circleRadius = 36;
          // Position circles to the right of the approval comments section
          const rightSectionX = 500; // Fixed position to the right
          const rightSectionY = approvalCommentsY - 10;
          const circleColors = [
            { fill: yellow, border: brown },
            { fill: [255, 240, 180], border: brown },
            { fill: [255, 240, 180], border: brown },
            { fill: [255, 240, 180], border: brown }
          ];
          // Draw circles in a row
          const numCircles = invoiceData.approvalCircles.length;
          const totalCirclesWidth = numCircles * (circleRadius * 2) + (numCircles - 1) * 24;
          const circlesStartX = rightSectionX;
          const circlesY = rightSectionY + circleRadius;

          invoiceData.approvalCircles.forEach((c, i) => {
            const cx = circlesStartX + i * (circleRadius * 2 + 24);
            const cy = circlesY;

            console.log("Status:",c.status);
            // Color coding based on status instead of showing text
            let circleColor = circleColors[i];
            if (c.checked) {
              // YES status - Green fill for approved
              circleColor = { fill: [46, 204, 113], border: brown }; // Green for approved
            } // else if (c.status && c.status.toLowerCase() === 'no') {
              // NO status - Red fill for rejected
             // circleColor = { fill: [231, 76, 60], border: brown }; // Red for rejected
           // } 
            else {
              // Pending/No response - White fill
              circleColor = { fill: [255, 255, 255], border: brown }; // White for pending
            }

            doc.setDrawColor(circleColor.border[0], circleColor.border[1], circleColor.border[2]);
            doc.setFillColor(circleColor.fill[0], circleColor.fill[1], circleColor.fill[2]);
            doc.circle(cx, cy, circleRadius, 'FD');
            // Dynamically adjust font size for label to fit inside the circle
            let labelFontSize = 13;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(labelFontSize);
            let labelWidth = doc.getTextWidth(c.label);
            while (labelWidth > (circleRadius * 1.7) && labelFontSize > 7) { // 1.7 to allow for padding
              labelFontSize -= 0.5;
              doc.setFontSize(labelFontSize);
              labelWidth = doc.getTextWidth(c.label);
            }
            doc.setTextColor(brown[0], brown[1], brown[2]);
            doc.text(c.label, cx, cy+6, {align:'center'});

            // No text shown - color indicates status
            // Green = Approved, Red = Rejected, Amber = Pending
          });

          // Draw Requestor and date directly below the circles, centered under the first circle
          // Move Requestor and date below the approval circles, centered under the first circle
          const firstCircleCx = circlesStartX;
          const lastCircleCx = circlesStartX + (numCircles - 1) * (circleRadius * 2 + 24);
          const circlesBottomY = circlesY + circleRadius + 8; // 8pt below the bottom of circles
          
          // Add "Hello" text with extra space above requestor
          const secondCircleCx = circlesStartX + (circleRadius * 2 + 24); // Position of second circle center (MI Internal, always index 1)
          const gbsCircleCx = circlesStartX + (numCircles - 1) * (circleRadius * 2 + 24); // GBS is always the last circle
          
          const helloTextY = circlesBottomY + 10; // 10pt below circles for "Hello"
          
          // Only show "Processed in X days" text if StatusId is 6
          if (invoiceData.statusId === 6) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(brown[0], brown[1], brown[2]);          
            const processedDays = invoiceData.miApproval.days; // fallback to 3 if no data
            doc.text(`Processed in ${processedDays} days`, secondCircleCx, helloTextY, { align: 'center' });
            const GBSDays = invoiceData.gbsApproval.days; // fallback to 3 if no data           
            // GBS is always the last circle regardless of whether MI Second Level is present
            doc.text(`Processed in ${GBSDays} days`, gbsCircleCx, helloTextY, { align: 'center' });
          }
          
          // Center the Requestor: text under the first circle
          const requestorLabel = 'Requestor: ';
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10.4);
          doc.setTextColor(brown[0], brown[1], brown[2]);
          const requestorLabelWidth = doc.getTextWidth(requestorLabel);
          // Move text further below and start under the first circle (not centered)
          const requestorTextY = circlesBottomY + 30; // 12pt more below circles
          const requestorTextX = firstCircleCx - 32;

          // Draw requestor label
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10.4);
          doc.setTextColor(brown[0], brown[1], brown[2]);
          doc.text(requestorLabel, requestorTextX, requestorTextY, {align:'left'});

          // Place the request date directly after the label (no name)
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9.6);
          doc.setTextColor(80, 80, 80);
          const requestDateX = requestorTextX + requestorLabelWidth + 6; // Small gap after label
          doc.text(invoiceData.requestDate, requestDateX, requestorTextY, {align:'left'});

          // Add MI Internal entry below Requestor with same styling and layout
          const miInternalY = requestorTextY + 20; // 20pt below requestor line
          const miInternalLabel = 'MI Internal: ';
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10.4);
          doc.setTextColor(brown[0], brown[1], brown[2]);
          const miInternalLabelWidth = doc.getTextWidth(miInternalLabel);
          doc.text(miInternalLabel, requestorTextX, miInternalY, {align:'left'});

          // MI Internal date directly after the label (no name)
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9.6);
          doc.setTextColor(80, 80, 80);
          const miInternalDateX = requestorTextX + miInternalLabelWidth + 6; // Small gap after label
          const miInternalDate = invoiceData.miApproverDate;
          doc.text(miInternalDate, miInternalDateX, miInternalY, {align:'left'});

          // Add GBS entry below MI Internal with same styling and layout
          const gbsY = miInternalY + 20; // 20pt below MI Internal line
          const gbsLabel = 'GBS: ';
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10.4);
          doc.setTextColor(brown[0], brown[1], brown[2]);
          const gbsLabelWidth = doc.getTextWidth(gbsLabel);
          doc.text(gbsLabel, requestorTextX, gbsY, {align:'left'});

          // GBS date directly after the label (no name)
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9.6);
          doc.setTextColor(80, 80, 80);
          const gbsDateX = requestorTextX + gbsLabelWidth + 6; // Small gap after label
          const gbsDate = invoiceData.gbsApproverDate;
          doc.text(gbsDate, gbsDateX, gbsY, {align:'left'});

          // Add extra space at the bottom for visual comfort (updated to use gbsY)
          const afterSectionY = Math.max(approvalCommentsBottomY, gbsY + 40);
          doc.setDrawColor(brown[0], brown[1], brown[2]);
          doc.setLineWidth(1);
          doc.line(40, afterSectionY + 40, pageWidth - 40, afterSectionY + 40);
      // Save as file
      const pdfBlob = doc.output('blob');
      saveAs(pdfBlob, `Digital_Copy_${adjReqId}.pdf`);

      this.spinnerService.hideSpinner();
      this.toastr.success('Digital copy PDF downloaded.', 'Success');

    } catch (error) {
      this.spinnerService.hideSpinner();
      this.toastr.error('Failed to generate or download PDF.', 'Error');
    }
  }
}
